import { useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthFlow } from '../../hooks/useAuthFlow';
import { useToast } from '../feedback/AppToast';
import { SMSCodeField } from './SMSCodeField';

export function AuthModal() {
  const auth = useAuth();
  const toast = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);
  const flow = useAuthFlow(auth.login);
  useEffect(() => {
    if (!auth.authModalOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const timer = window.setTimeout(
      () => dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus(),
      0
    );
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') auth.closeAuth();
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button,input')].filter(
        (el) => !el.hasAttribute('disabled')
      );
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [auth.authModalOpen]);
  if (!auth.authModalOpen) return null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await flow.submit()) toast('登录成功，将继续之前的操作', 'success');
  };
  return (
    <div
      className="modal-backdrop show"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) auth.closeAuth();
      }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
      >
        <div className="modal-row">
          <div>
            <h2 id="auth-title">手机号登录</h2>
            <p>使用独立文章评测账号，不会跳转或复用 GEO 登录。</p>
          </div>
          <button className="icon-button" aria-label="关闭登录弹窗" onClick={auth.closeAuth}>
            ×
          </button>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <SMSCodeField
            mobile={flow.mobile}
            code={flow.code}
            countdown={flow.countdown}
            sending={flow.sending}
            error={flow.error}
            onMobile={flow.setMobile}
            onCode={flow.setCode}
            onSend={flow.send}
          />
          <label>
            所在企业（首次登录必填）
            <input
              value={flow.companyName}
              maxLength={50}
              onChange={(e) => flow.setCompanyName(e.target.value)}
              placeholder="例如：示例科技"
            />
          </label>
          {flow.captchaRequired && (
            <label>
              安全验证 Token
              <input
                value={flow.captchaToken}
                maxLength={2048}
                onChange={(e) => flow.setCaptchaToken(e.target.value)}
                placeholder="完成安全验证后填写"
              />
            </label>
          )}
          <button className="btn large" disabled={flow.submitting}>
            {flow.submitting ? '登录中…' : '登录并继续'}
          </button>
          <p className="privacy-copy">验证码与手机号仅用于本网站认证，不与 GEO 账号体系共享。</p>
        </form>
      </div>
    </div>
  );
}
